/**
 * Phase 56 Plan 02 — Edit modal for cron job schedule override + disable.
 *
 * Wraps common/Modal to get Escape-to-close, click-outside-to-close,
 * body-scroll-lock, and ARIA for free (Pitfall-1 mitigation).
 *
 * Security: all user-provided values rendered as React text nodes (auto-escaped).
 * T-08: raw-HTML injection is prohibited — use JSX text nodes only.
 *
 * Initial disabled state is derived from row.disabled — opening the modal on a
 * currently-disabled job shows the checkbox checked, preventing accidental re-enable.
 */
import { useState } from 'react';
import { Modal } from '../common/Modal';
import { useUpdateCronJobOverride, useResetCronJobOverride } from '../../hooks/useCronJobs';
import type { CronJobRow } from '../../services/api/cronJobs';

interface Props {
  row: CronJobRow;
  onClose: () => void;
  onSaved: () => void;
}

export function CronJobEditModal({ row, onClose, onSaved }: Props) {
  const isOverridden = row.pattern !== null && row.pattern !== row.defaultPattern;

  const [pattern, setPattern] = useState<string>(row.pattern ?? row.defaultPattern ?? '');
  const [disabled, setDisabled] = useState<boolean>(row.disabled ?? false);
  const [error, setError] = useState<string | null>(null);

  const updateMutation = useUpdateCronJobOverride(row.schedulerId);
  const resetMutation = useResetCronJobOverride(row.schedulerId);

  const handleSave = async () => {
    setError(null);
    try {
      await updateMutation.mutateAsync({ cronPattern: pattern, disabled });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save override');
    }
  };

  const handleReset = async () => {
    setError(null);
    try {
      await resetMutation.mutateAsync();
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset override');
    }
  };

  const footer = (
    <div className="flex items-center justify-between">
      {/* Left side: Reset to default */}
      <button
        type="button"
        onClick={handleReset}
        disabled={!isOverridden || resetMutation.isPending || updateMutation.isPending}
        className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        title={isOverridden ? 'Remove override and restore registry default' : 'No override active'}
      >
        Reset to default
      </button>

      {/* Right side: Cancel + Save */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={updateMutation.isPending || resetMutation.isPending}
          className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={updateMutation.isPending || resetMutation.isPending}
          className="text-sm px-3 py-1.5 rounded bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {updateMutation.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Edit schedule: ${row.schedulerId}`}
      size="md"
      ariaLabelledBy="cron-edit-title"
      footer={footer}
    >
      <div className="space-y-4">
        {/* Default pattern reference */}
        <div className="text-sm text-gray-600">
          <span>Default: </span>
          <code className="font-mono bg-gray-100 px-1 py-0.5 rounded text-xs">
            {row.defaultPattern ?? '—'}
          </code>
          {isOverridden && (
            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-800">
              overridden
            </span>
          )}
        </div>

        {/* Cron pattern input */}
        <div>
          <label
            htmlFor="cron-pattern-input"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Cron pattern
          </label>
          <input
            id="cron-pattern-input"
            type="text"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            placeholder={row.defaultPattern ?? ''}
            className="w-full font-mono text-sm border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Disable checkbox */}
        <div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={disabled}
              onChange={(e) => setDisabled(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Disable this job (no scheduled runs)</span>
          </label>
          <p className="mt-1 ml-6 text-xs text-gray-500">
            Disable stops future scheduled runs. Currently-running jobs finish normally.
          </p>
        </div>

        {/* Inline error */}
        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
